import React, { Dispatch, SetStateAction, useEffect } from "react";

import { ProductTileModel } from "@api/mappers/products.mapper";

import { getDamPath } from "@utils/configuration/aem";

import { useGranite } from "@redux/modules/value/translations/translations.provider";

import CalculationField from "@components/Calculator/CalculationField/calculationField.component";
import { Dropdown } from "@components/General/Dropdown/dropdown.component";
import { DropdownItem } from "@components/General/Dropdown/dropdownItem.component";
import { Input } from "@components/General/Form/input.component";
import { Label } from "@components/General/Form/label.component";
import { Col } from "@components/General/Grid/col.component";
import { Row } from "@components/General/Grid/row.component";

import { RoofShape, RoofShapeProperties } from "../epdmCalculator";

type Props = {
    setRoofShapeProperties: Dispatch<SetStateAction<RoofShapeProperties>>;
    setRoofShape: Dispatch<SetStateAction<RoofShape>>;
    setIsolationProduct: Dispatch<SetStateAction<undefined | ProductTileModel>>;
    roofShapeProperties: RoofShapeProperties;
    roofShape: RoofShape;
    isolationProduct?: ProductTileModel;
    isolationProducts: Array<ProductTileModel>;
    isolationRequired: boolean;
    setIsolationRequired: Dispatch<SetStateAction<boolean>>;
};

const Calculation = ({
    setRoofShapeProperties,
    roofShapeProperties,
    roofShape,
    setRoofShape,
    isolationProducts,
    isolationProduct,
    setIsolationProduct,
    isolationRequired,
    setIsolationRequired,
}: Props) => {
    const { t } = useGranite();

    const handleCalculationValue = (key: string, value: string) => {
        setRoofShapeProperties(state => ({
            ...state,
            [key]: value,
        }));
    };

    useEffect(() => {
        if (
            isolationRequired &&
            !isolationProduct &&
            isolationProducts.length > 0
        ) {
            setIsolationProduct(isolationProducts.at(0));
        }
    }, [isolationRequired, isolationProduct, isolationProducts]);

    return (
        <div className={"mt-4 calculator"}>
            <Row>
                <Col className={"mt-4"} md={6}>
                    <h3 className="d-sm-block">
                        {t("calculator.epdmCalculator.roofShape")}
                    </h3>
                    <Dropdown
                        placeholder={
                            roofShape === RoofShape.RECTANGLE
                                ? t(
                                      "calculator.epdmCalculator.roofShape.rectangle",
                                  )
                                : t(
                                      "calculator.epdmCalculator.roofShape.lShape",
                                  )
                        }
                    >
                        <DropdownItem
                            onClick={() => setRoofShape(RoofShape.RECTANGLE)}
                            label={t(
                                "calculator.epdmCalculator.roofShape.rectangle",
                            )}
                        />
                        <DropdownItem
                            onClick={() => setRoofShape(RoofShape.L_SHAPED)}
                            label={t(
                                "calculator.epdmCalculator.roofShape.lShape",
                            )}
                        />
                    </Dropdown>
                    <h3 className="d-sm-block mt-4 mb-2">
                        {t("calculator.epdmCalculator.requiredMaterial")}
                    </h3>
                    <Label for="isolation" check>
                        <Input
                            type="checkbox"
                            id="isolation"
                            checked={isolationRequired}
                            onChange={e => {
                                setIsolationRequired(
                                    (e.target as HTMLInputElement).checked,
                                );
                            }}
                        />{" "}
                        {t("calculator.epdmCalculator.isolation")}
                    </Label>
                    {isolationRequired && isolationProducts.length > 0 && (
                        <Dropdown
                            className="mt-1"
                            placeholder={
                                isolationProduct?.title ||
                                isolationProducts.at(0)?.title
                            }
                        >
                            {isolationProducts.map(p => (
                                <DropdownItem
                                    key={p.sku}
                                    onClick={() => setIsolationProduct(p)}
                                    label={p.title}
                                />
                            ))}
                        </Dropdown>
                    )}
                </Col>
                <Col className={"mt-4"} md={6}>
                    {roofShape === RoofShape.L_SHAPED && (
                        <img
                            className={"mt-3 calculator-image--large"}
                            src={`${getDamPath()}/hulp-en-advies/calculators/calculator-aquaplan-L-vormig-dak-epdm.png`}
                            alt={"calculator-aquaplan-L-vormig-dak-epdm.png"}
                        />
                    )}
                    {roofShape === RoofShape.RECTANGLE && (
                        <img
                            className={"mt-3 calculator-image--large"}
                            src={`${getDamPath()}/hulp-en-advies/calculators/calculator-aquaplan-rechthoekig-dak-epdm.png`}
                            alt={"calculator-aquaplan-rechthoekig-dak-epdm.png"}
                        />
                    )}
                </Col>
            </Row>
            <h3 className="d-sm-block mt-4 mb-0">
                {roofShape === RoofShape.RECTANGLE
                    ? t("calculator.epdmCalculator.measurementsRectangularRoof")
                    : t(
                          "calculator.epdmCalculator.measurementsLShapedRoofPart1",
                      )}
            </h3>
            <Row>
                <CalculationField
                    name="widthA1"
                    dimension="widthA1"
                    unit="cm"
                    setCalculationValue={handleCalculationValue}
                    {...(roofShapeProperties.widthA1 && {
                        value: roofShapeProperties.widthA1,
                    })}
                    sm={6}
                    md={3}
                    lg={3}
                />
                <CalculationField
                    name="heightB1"
                    dimension="heightB1"
                    unit="cm"
                    setCalculationValue={handleCalculationValue}
                    {...(roofShapeProperties.heightB1 && {
                        value: roofShapeProperties.heightB1,
                    })}
                    sm={6}
                    md={3}
                    lg={3}
                />
                <CalculationField
                    name="lengthC1"
                    dimension="lengthC1"
                    unit="cm"
                    setCalculationValue={handleCalculationValue}
                    {...(roofShapeProperties.lengthC1 && {
                        value: roofShapeProperties.lengthC1,
                    })}
                    sm={6}
                    md={3}
                    lg={3}
                />
                <CalculationField
                    name="widthW1"
                    dimension="widthW1"
                    unit="cm"
                    setCalculationValue={handleCalculationValue}
                    {...(roofShapeProperties.widthW1 && {
                        value: roofShapeProperties.widthW1,
                    })}
                    sm={6}
                    md={3}
                    lg={3}
                />
            </Row>
            {roofShape === RoofShape.L_SHAPED && (
                <>
                    <h3 className="d-sm-block mt-4 mb-0">
                        {t(
                            "calculator.epdmCalculator.measurementsLShapedRoofPart2",
                        )}
                    </h3>
                    <Row>
                        <CalculationField
                            name="lengthC2"
                            dimension="lengthC2"
                            unit="cm"
                            setCalculationValue={handleCalculationValue}
                            {...(roofShapeProperties.lengthC2 && {
                                value: roofShapeProperties.lengthC2,
                            })}
                            sm={6}
                            md={3}
                            lg={3}
                        />
                        <CalculationField
                            name="widthW2"
                            dimension="widthW2"
                            unit="cm"
                            setCalculationValue={handleCalculationValue}
                            {...(roofShapeProperties.widthW2 && {
                                value: roofShapeProperties.widthW2,
                            })}
                            sm={6}
                            md={3}
                            lg={3}
                        />
                    </Row>
                </>
            )}
        </div>
    );
};

export default Calculation;
